/**
 * PremiumTabs.jsx
 * Premium styled tabs component using design tokens
 *
 * Features:
 * - Icons with labels
 * - Premium glassmorphism styling
 * - Animated indicator
 * - Dark/Light mode support
 * - Mobile responsive
 *
 * Usage:
 * <PremiumTabs
 *   tabs={[
 *     { label: 'Geral', icon: <InfoIcon /> },
 *     { label: 'Veículo', icon: <DirectionsCarIcon /> },
 *   ]}
 *   value={activeTab}
 *   onChange={(e, newValue) => setActiveTab(newValue)}
 * />
 */

import React from 'react';
import {
 Tabs, Tab, Box, useMediaQuery, useTheme 
} from '@mui/material';
import { usePremiumTokens } from '../theme/usePremiumTokens';

const PremiumTabs = ({
  tabs,
  value,
  onChange,
  variant = 'standard', // 'standard' | 'fullWidth' | 'scrollable'
  centered = false,
  size = 'medium', // 'small' | 'medium' | 'large'
}) => {
  const tokens = usePremiumTokens();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Safe token access with fallbacks
  const safeTokens = {
    border: {
      subtle: tokens?.border?.subtle || 'rgba(255, 255, 255, 0.06)',
    },
    surface: {
      glass: tokens?.surface?.glass || 'rgba(255, 255, 255, 0.03)',
    },
    radius: {
      md: tokens?.radius?.md || '8px',
      lg: tokens?.radius?.lg || '12px',
    },
    text: {
      primary: tokens?.text?.primary || '#f0f6fc',
      secondary: tokens?.text?.secondary || '#8b949e',
      muted: tokens?.text?.muted || '#6e7681',
    },
    accent: {
      primary: tokens?.accent?.primary || '#00d4aa',
      subtle: tokens?.accent?.subtle || 'rgba(0, 212, 170, 0.1)',
      glow: tokens?.accent?.glow || 'rgba(0, 212, 170, 0.3)',
    },
    transitions: {
      all: tokens?.transitions?.all || 'all 0.2s ease',
    },
    typography: {
      fontFamily: {
        body: tokens?.typography?.fontFamily?.body || 'Inter, sans-serif',
      },
    },
    isDark: tokens?.isDark ?? true,
  };

  // Size configurations
  const sizeConfig = {
    small: {
      minHeight: 40,
      fontSize: '0.75rem',
      iconSize: 16,
      padding: '6px 12px',
      gap: 0.5,
    },
    medium: {
      minHeight: 48,
      fontSize: '0.875rem',
      iconSize: 18,
      padding: '8px 16px',
      gap: 1,
    },
    large: {
      minHeight: 56,
      fontSize: '1rem',
      iconSize: 20,
      padding: '12px 20px',
      gap: 1.5,
    },
  };

  const config = sizeConfig[size] || sizeConfig.medium;

  return (
    <Box
      sx={{
        borderBottom: `1px solid ${safeTokens.border.subtle}`,
        mb: 3,
        position: 'relative',
        // Glassmorphism background for tabs container
        backgroundColor: safeTokens.surface.glass,
        borderRadius: `${safeTokens.radius.lg} ${safeTokens.radius.lg} 0 0`,
      }}
    >
      <Tabs
        value={value}
        onChange={onChange}
        variant={isMobile ? 'scrollable' : variant}
        scrollButtons={isMobile ? 'auto' : false}
        centered={!isMobile && centered}
        sx={{
          minHeight: config.minHeight,
          '& .MuiTabs-flexContainer': {
            gap: 0.5,
          },
          '& .MuiTabs-scroller': {
            // Scrollbar styling for mobile
            '&::-webkit-scrollbar': {
              display: 'none',
            },
            scrollbarWidth: 'none',
          },
          '& .MuiTab-root': {
            color: safeTokens.text.secondary,
            fontFamily: safeTokens.typography.fontFamily.body,
            fontWeight: 500,
            fontSize: config.fontSize,
            textTransform: 'none',
            minHeight: config.minHeight,
            padding: config.padding,
            borderRadius: `${safeTokens.radius.md} ${safeTokens.radius.md} 0 0`,
            transition: safeTokens.transitions.all,
            position: 'relative',
            overflow: 'hidden',
            // Icon styling
            '& .MuiTab-iconWrapper': {
              marginRight: theme.spacing(config.gap),
              fontSize: config.iconSize,
              transition: safeTokens.transitions.all,
            },
            // Hover effect
            '&:hover': {
              color: safeTokens.text.primary,
              backgroundColor: safeTokens.surface.glass,
              '& .MuiTab-iconWrapper': {
                color: safeTokens.accent.primary,
              },
            },
            // Selected state
            '&.Mui-selected': {
              color: safeTokens.accent.primary,
              fontWeight: 600,
              backgroundColor: safeTokens.isDark
                ? 'rgba(0, 212, 170, 0.08)'
                : 'rgba(0, 168, 132, 0.08)',
              '& .MuiTab-iconWrapper': {
                color: safeTokens.accent.primary,
              },
            },
            // Disabled state
            '&.Mui-disabled': {
              color: safeTokens.text.muted,
              opacity: 0.5,
            },
            // Focus visible
            '&.Mui-focusVisible': {
              backgroundColor: safeTokens.accent.subtle,
              outline: `2px solid ${safeTokens.accent.primary}`,
              outlineOffset: -2,
            },
          },
          // Indicator (underline)
          '& .MuiTabs-indicator': {
            backgroundColor: safeTokens.accent.primary,
            height: 3,
            borderRadius: '3px 3px 0 0',
            // Glow effect
            boxShadow: `0 0 8px ${safeTokens.accent.glow}`,
          },
          // Scroll buttons styling
          '& .MuiTabScrollButton-root': {
            color: safeTokens.text.secondary,
            opacity: 1,
            '&.Mui-disabled': {
              opacity: 0.3,
            },
            '&:hover': {
              color: safeTokens.accent.primary,
              backgroundColor: safeTokens.surface.glass,
            },
          },
        }}
      >
        {tabs.map((tab, index) => (
          <Tab
            key={tab.id || index}
            icon={tab.icon}
            label={tab.label}
            iconPosition="start"
            disabled={tab.disabled}
            id={`premium-tab-${index}`}
            aria-controls={`premium-tabpanel-${index}`}
          />
        ))}
      </Tabs>
    </Box>
  );
};

/**
 * TabPanel component for content
 */
export const PremiumTabPanel = ({
  children,
  value,
  index,
  keepMounted = false,
  ...other
}) => {
  const tokens = usePremiumTokens();
  const isActive = value === index;

  if (!keepMounted && !isActive) {
    return null;
  }

  return (
    <Box
      role="tabpanel"
      hidden={!isActive}
      id={`premium-tabpanel-${index}`}
      aria-labelledby={`premium-tab-${index}`}
      sx={{
        py: 2,
        // Fade animation
        opacity: isActive ? 1 : 0,
        transition: 'opacity 0.2s ease-in-out',
        // Content styling
        '& .MuiTextField-root': {
          mb: 2,
        },
      }}
      {...other}
    >
      {(keepMounted || isActive) && children}
    </Box>
  );
};

/**
 * Vertical variant of PremiumTabs
 */
export const PremiumVerticalTabs = ({
  tabs,
  value,
  onChange,
  size = 'medium',
}) => {
  const tokens = usePremiumTokens();

  // Safe token access with fallbacks
  const safeTokens = {
    border: {
      subtle: tokens?.border?.subtle || 'rgba(255, 255, 255, 0.06)',
    },
    surface: {
      glass: tokens?.surface?.glass || 'rgba(255, 255, 255, 0.03)',
    },
    radius: {
      lg: tokens?.radius?.lg || '12px',
    },
    text: {
      primary: tokens?.text?.primary || '#f0f6fc',
      secondary: tokens?.text?.secondary || '#8b949e',
    },
    accent: {
      primary: tokens?.accent?.primary || '#00d4aa',
    },
    transitions: {
      all: tokens?.transitions?.all || 'all 0.2s ease',
    },
    isDark: tokens?.isDark ?? true,
  };

  const sizeConfig = {
    small: { minHeight: 36, fontSize: '0.75rem', iconSize: 16 },
    medium: { minHeight: 44, fontSize: '0.875rem', iconSize: 18 },
    large: { minHeight: 52, fontSize: '1rem', iconSize: 20 },
  };

  const config = sizeConfig[size] || sizeConfig.medium;

  return (
    <Box
      sx={{
        borderRight: `1px solid ${safeTokens.border.subtle}`,
        minWidth: 180,
        backgroundColor: safeTokens.surface.glass,
        borderRadius: `${safeTokens.radius.lg} 0 0 ${safeTokens.radius.lg}`,
      }}
    >
      <Tabs
        orientation="vertical"
        value={value}
        onChange={onChange}
        sx={{
          '& .MuiTab-root': {
            color: safeTokens.text.secondary,
            fontWeight: 500,
            fontSize: config.fontSize,
            textTransform: 'none',
            minHeight: config.minHeight,
            justifyContent: 'flex-start',
            textAlign: 'left',
            px: 2,
            borderRadius: 0,
            transition: safeTokens.transitions.all,
            '& .MuiTab-iconWrapper': {
              marginRight: 1.5,
              fontSize: config.iconSize,
            },
            '&:hover': {
              color: safeTokens.text.primary,
              backgroundColor: safeTokens.surface.glass,
            },
            '&.Mui-selected': {
              color: safeTokens.accent.primary,
              fontWeight: 600,
              backgroundColor: safeTokens.isDark
                ? 'rgba(0, 212, 170, 0.08)'
                : 'rgba(0, 168, 132, 0.08)',
            },
          },
          '& .MuiTabs-indicator': {
            left: 0,
            right: 'auto',
            width: 3,
            backgroundColor: safeTokens.accent.primary,
            borderRadius: '0 3px 3px 0',
          },
        }}
      >
        {tabs.map((tab, index) => (
          <Tab
            key={tab.id || index}
            icon={tab.icon}
            label={tab.label}
            iconPosition="start"
            disabled={tab.disabled}
          />
        ))}
      </Tabs>
    </Box>
  );
};

export default PremiumTabs;
